import 'reflect-metadata'
import { ComponentType } from 'react'
import { IFormInputBase } from './types'
import { IConfigValue, IFormValidatorConfig } from './validator'

const ClassRegistry = new Map<string, any>()

export function registerForm(name?: string) {
  return function <T extends { new (...args: any[]): {} }>(constructor: T) {
    const key = name || constructor.name
    ClassRegistry.set(key, constructor)
    class RegisteredClass extends constructor {
      __class = key
      constructor(...args: any[]) {
        super(...args)
        this.__class = key
      }
    }
    Object.defineProperty(RegisteredClass, 'name', { value: constructor.name })
    Object.defineProperty(RegisteredClass, '__class', { value: key })
    return RegisteredClass
  }
}

/**
 * Check whether a class (constructor) or an instance has been registered
 * with the @registerForm decorator.
 *
 * Accepts:
 * - the decorated constructor (function)
 * - the original constructor (function)
 * - an instance of the class
 *
 * Returns true when the underlying registry contains an entry for the
 * class key (either the custom name provided to the decorator or the
 * constructor name).
 */
export function isFormRegistered(target: any): boolean {
  if (!target) return false
  const ctor = typeof target === 'function' ? target : target?.constructor
  if (!ctor) return false
  const key = (ctor as any).__class || (ctor as any).name
  if (!key) return false
  return ClassRegistry.has(key)
}

export interface IPreSubmitMetadata<T = any> {
  handler: (data: T, oldData: T) => Promise<any> | any
}

export function formPreSubmit<T = any>(handler: IPreSubmitMetadata<T>['handler']) {
  return function <T extends { new (...args: any[]): {} }>(constructor: T) {
    Reflect.defineMetadata('form:presubmit', { handler }, constructor)
    return constructor
  }
}

export function getFormPreSubmit(targetClass: any): IPreSubmitMetadata | undefined {
  return Reflect.getMetadata('form:presubmit', targetClass)
}

export interface IFieldMetadata<T = any, O = {}> {
  name: keyof T | string
  label?: string
  placeholder?: string
  defaultValue?: any
  type?: any
  size?: Partial<Record<'xs' | 'sm' | 'md' | 'lg' | 'xl', number>>
  visible?: boolean | ((data?: T) => boolean)
  disabled?: boolean | ((data?: T) => boolean)
  renderInput?: ComponentType<IFormInputBase<T>>
}

export function formField(options: Omit<IFieldMetadata, 'name'>) {
  return function (target: any, propertyKey: string) {
    const designType = Reflect.getMetadata('design:type', target, propertyKey)
    const actualType = options.type || designType

    // Get existing fields metadata - use hasOwnMetadata to avoid parent pollution
    const isMetaData = Reflect.hasOwnMetadata('form:fields', target.constructor)
    const existing: IFieldMetadata[] = isMetaData ? Reflect.getOwnMetadata('form:fields', target.constructor) : []

    // Check if this field already exists (override case)
    const existingIndex = existing.findIndex((f) => f.name === propertyKey)

    // Get default value from property if not explicitly provided
    // Need to create a temporary instance to get the initialized value
    let defaultValue = options.defaultValue
    if (defaultValue === undefined) {
      try {
        const tempInstance = new (target.constructor as any)()
        defaultValue = tempInstance[propertyKey]
      } catch {
        // If constructor fails, use undefined
        defaultValue = undefined
      }
    }

    const newField: IFieldMetadata = { name: propertyKey, type: actualType, label: options.label || propertyKey, ...options, defaultValue }

    if (existingIndex >= 0) {
      // Override existing field, keep position
      existing[existingIndex] = newField
      Reflect.defineMetadata('form:fields', existing, target.constructor)
    } else {
      // New field, append to end
      const fields = [...existing, newField]
      Reflect.defineMetadata('form:fields', fields, target.constructor)
    }
  }
}

export const getFormFields = (targetClass: any): IFieldMetadata[] => {
  // Get parent class
  const parentClass = Object.getPrototypeOf(targetClass)

  // Get parent fields recursively
  const parentFields: IFieldMetadata[] = parentClass && parentClass !== Function.prototype ? getFormFields(parentClass) : []

  // Get current class fields
  const currentFields: IFieldMetadata[] = Reflect.getMetadata('form:fields', targetClass) || []

  // If no parent, return current fields directly
  if (parentFields.length === 0) {
    return currentFields
  }

  // Merge parent and current fields
  // Start with parent fields as base
  const mergedFields = [...parentFields]
  const fieldNames = new Set(parentFields.map((f) => f.name.toString()))

  // Process current class fields
  currentFields.forEach((currentField) => {
    const fieldName = currentField.name.toString()

    if (fieldNames.has(fieldName)) {
      // Override parent field - find and replace it in place
      const index = mergedFields.findIndex((f) => f.name.toString() === fieldName)
      if (index >= 0) {
        mergedFields[index] = currentField
      }
    } else {
      // New field - append to end
      mergedFields.push(currentField)
      fieldNames.add(fieldName)
    }
  })

  return mergedFields
}

export interface IValidationMetadata extends IConfigValue {
  name: string
}

export function formValidate(options: Omit<IValidationMetadata, 'name'>) {
  return function (target: any, propertyKey: string) {
    const existing: IValidationMetadata[] = Reflect.getMetadata('form:validators', target.constructor) || []
    const validators = [...existing]
    validators.push({ name: propertyKey, ...options })
    Reflect.defineMetadata('form:validators', validators, target.constructor)
  }
}

export function getFormValidators<T = any>(targetClass: any): IFormValidatorConfig<T> {
  const metas: IValidationMetadata[] = Reflect.getMetadata('form:validators', targetClass) || []
  return metas.reduce<IFormValidatorConfig<T>>((acc, curr) => {
    acc[curr.name as keyof T] = { Rules: curr.Rules, label: curr.label }
    return acc
  }, {})
}
